import { flushActivityLog, flushActivityLogKeepalive } from 'src/utilities/LanternApiClient';

// Lightweight client-side activity tracking (in-memory rolling buffer).
const BUFFER_LIMIT = 150;
const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // close the passive session after 10 minutes idle
const API_SLOW_THRESHOLD_MS = 10_000;
const GROUP_KEY = 'lantern:activity-group';
const TAB_KEY = 'lantern:activity-tab';
const LEADER_KEY = 'lantern:activity-leader';
const GROUP_IDLE_RESET_MS = 30 * 60 * 1000; // 30 minutes
const LEADER_HEARTBEAT_MS = 5000;
const LEADER_STALE_MS = 15000;

let buffer = [];
let initialized = false;
let sessionId = null;
let startedAt = null;
let lastActivity = null;
let groupId = null;
let tabId = null;
let flushedEventCount = 0;
let leaderHeartbeatId = null;
let flushInFlight = Promise.resolve();
let isLeader = false;

const generateSecureId = () => {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  }

  throw new Error('Secure random ID generation is unavailable in this browser.');
};

const getUserEmailFromToken = () => {
  try {
    const token = localStorage.getItem('auth');
    if (!token) return undefined;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.upn || payload?.email || payload?.preferred_username;
  } catch {
    return undefined;
  }
};

const loadGroupState = () => {
  try {
    const stored = localStorage.getItem(GROUP_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const persistGroupState = (state) => {
  try {
    localStorage.setItem(GROUP_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
};

const ensureGroupId = () => {
  const now = Date.now();
  const state = loadGroupState();
  if (state?.groupId && now - (state.lastUsed || 0) <= GROUP_IDLE_RESET_MS) {
    const nextState = { groupId: state.groupId, lastUsed: now };
    persistGroupState(nextState);
    return state.groupId;
  }
  const newId = generateSecureId();
  persistGroupState({ groupId: newId, lastUsed: now });
  return newId;
};

const ensureTabId = () => {
  try {
    const stored = sessionStorage.getItem(TAB_KEY);
    if (stored) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }

  const newId = generateSecureId();
  try {
    sessionStorage.setItem(TAB_KEY, newId);
  } catch {
    // ignore storage errors
  }

  return newId;
};

const loadLeaderState = () => {
  try {
    const stored = localStorage.getItem(LEADER_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const persistLeaderState = (state) => {
  try {
    if (!state) {
      localStorage.removeItem(LEADER_KEY);
      return;
    }

    localStorage.setItem(LEADER_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
};

const isLeaderStateActive = (state, now = Date.now()) => Boolean(state?.tabId) && now - (state.lastSeen || 0) <= LEADER_STALE_MS;

const stopLeaderHeartbeat = () => {
  if (leaderHeartbeatId) {
    window.clearInterval(leaderHeartbeatId);
    leaderHeartbeatId = null;
  }
};

const heartbeatLeadership = () => {
  if (!isLeader) {
    return;
  }

  persistLeaderState({ tabId, lastSeen: Date.now() });
};

const startNewSession = (setActive = true) => {
  const now = Date.now();
  sessionId = generateSecureId();
  startedAt = now;
  flushedEventCount = 0;
  if (setActive) {
    lastActivity = now;
  }
};

const toIso = (value) => {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return new Date(value).toISOString();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : new Date(parsed).toISOString();
  }
  return new Date().toISOString();
};

const normalizeEvent = (event) => {
  if (!event) return null;
  const clone = { ...event };
  delete clone.groupId;
  delete clone.sessionId;
  delete clone.userAgent;
  clone.ts = toIso(clone.ts || Date.now());
  return clone;
};

const buildEvent = (event) =>
  normalizeEvent({
    ts: Date.now(),
    tabId,
    ...event,
  });

const buildFlushPayload = (reason) => {
  if (!sessionId || !startedAt) {
    return null;
  }

  const pendingEvents = buffer.slice(flushedEventCount).map(normalizeEvent).filter(Boolean);
  if (!pendingEvents.length) {
    return null;
  }

  return {
    sessionId,
    groupId,
    startedAt: new Date(startedAt).toISOString(),
    updatedAt: new Date(Date.now()).toISOString(),
    userEmail: getUserEmailFromToken(),
    eventCount: buffer.length,
    reason,
    events: pendingEvents,
  };
};

const trimBufferToLimit = () => {
  if (buffer.length <= BUFFER_LIMIT) {
    return;
  }

  const overflow = buffer.length - BUFFER_LIMIT;
  buffer = buffer.slice(overflow);
  flushedEventCount = Math.max(0, flushedEventCount - overflow);
};

const finalizeSession = (resetLastActivity = false) => {
  buffer = [];
  sessionId = null;
  startedAt = null;
  flushedEventCount = 0;
  if (resetLastActivity) {
    lastActivity = null;
  }
};

const markUserActivity = (now = Date.now()) => {
  lastActivity = now;
  ensureGroupId();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('activity:update'));
  }
};

const appendSessionStart = () => {
  buffer.push(
    buildEvent({
      type: 'session_start',
      route: window.location.pathname,
    }),
  );
};

const appendSessionEnd = (reason) => {
  if (!sessionId) {
    return;
  }

  buffer.push(
    buildEvent({
      type: 'session_end',
      route: window.location.pathname,
      reason,
    }),
  );
};

const assumeLeadership = () => {
  if (isLeader) {
    heartbeatLeadership();
    return;
  }

  isLeader = true;
  heartbeatLeadership();
  stopLeaderHeartbeat();
  leaderHeartbeatId = window.setInterval(heartbeatLeadership, LEADER_HEARTBEAT_MS);
};

const releaseLeadership = () => {
  if (!isLeader) {
    return;
  }

  isLeader = false;
  stopLeaderHeartbeat();

  const leaderState = loadLeaderState();
  if (leaderState?.tabId === tabId) {
    persistLeaderState(null);
  }
};

const claimLeadership = ({ force = false } = {}) => {
  const now = Date.now();
  const currentLeader = loadLeaderState();
  const shouldClaim =
    force || !isLeaderStateActive(currentLeader, now) || currentLeader?.tabId === tabId || document.visibilityState === 'visible';

  if (!shouldClaim) {
    return false;
  }

  assumeLeadership();
  return true;
};

const shouldFlushImmediately = (event) => {
  if (!event) {
    return false;
  }

  if (event.type !== 'api') {
    return false;
  }

  const status = event.api?.status ?? 0;
  const latencyMs = event.api?.latencyMs ?? 0;
  return status === 0 || status >= 400 || latencyMs >= API_SLOW_THRESHOLD_MS;
};

const getApiTrackingMode = ({ method, status, latencyMs, isBackground }) => {
  if (isBackground) {
    return 'ignore';
  }

  if (status === 0 || status >= 400) {
    return 'keep';
  }

  if (latencyMs >= API_SLOW_THRESHOLD_MS) {
    return 'keep';
  }

  if (method !== 'GET') {
    return 'keep';
  }

  return 'ignore';
};

export const initActivityTracking = () => {
  if (initialized) return;
  initialized = true;
  groupId = ensureGroupId();
  tabId = ensureTabId();
  claimLeadership({ force: true });
  if (isLeader) {
    startNewSession();
    appendSessionStart();
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== LEADER_KEY) {
      return;
    }

    let nextLeader = null;
    try {
      nextLeader = event.newValue ? JSON.parse(event.newValue) : null;
    } catch {
      nextLeader = null;
    }

    if (!isLeader || nextLeader?.tabId === tabId) {
      return;
    }

    if (sessionId && flushedEventCount > 0) {
      appendSessionEnd('leader_handoff');
      flushActivity('leader_handoff', true).finally(() => releaseLeadership());
      return;
    }

    finalizeSession(true);
    releaseLeadership();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      claimLeadership({ force: true });
      return;
    }

    if (document.visibilityState === 'hidden' && isLeader && !sessionId) {
      releaseLeadership();
    }
  });

  window.addEventListener('pagehide', () => {
    const shouldFlushOnUnload = sessionId && flushedEventCount > 0;
    releaseLeadership();

    if (shouldFlushOnUnload) {
      appendSessionEnd('unload');
    }

    const payload = shouldFlushOnUnload ? buildFlushPayload('unload') : null;
    if (!payload) {
      finalizeSession(true);
      return;
    }

    flushActivityLogKeepalive(payload);
    finalizeSession(true);
  });
};

export const trackEvent = (event, { background = false } = {}) => {
  if (!initialized) return;
  const now = Date.now();
  if (!background) {
    claimLeadership({ force: true });
  }

  if (!isLeader) {
    return;
  }

  if (!sessionId) {
    if (background) {
      return;
    }

    startNewSession(true);
    appendSessionStart();
  }

  if (!background) {
    const isIdle = lastActivity && now - lastActivity > IDLE_TIMEOUT_MS;
    if (isIdle) {
      finalizeSession(false);
      startNewSession();
      appendSessionStart();
    }
  }

  const baseEvent = background ? { ...event, background: true } : event;
  buffer.push(buildEvent(baseEvent));
  trimBufferToLimit();
  if (!background) {
    markUserActivity(now);
  }

  if (shouldFlushImmediately(baseEvent)) {
    flushActivity(`${baseEvent.type}_important`, false);
  }
};

export const getRecentActivity = () => [...buffer];

export const getTrackingContext = () => ({
  sessionId,
  groupId,
  tabId,
  isLeader,
  activityEventCount: buffer.length,
});

export const flushActivity = (reason = 'manual', clearAfter = false) => {
  flushInFlight = flushInFlight
    .catch(() => null)
    .then(async () => {
      if (!isLeader) {
        return null;
      }

      const payload = buildFlushPayload(reason);
      if (!payload) return null;

      try {
        const response = await flushActivityLog(payload);
        if (!response?.success) {
          return payload;
        }

        flushedEventCount = buffer.length;

        if (clearAfter) {
          finalizeSession(reason === 'unload');
        }

        return payload;
      } catch (_error) {
        return payload;
      }
    });

  return flushInFlight;
};

export const instrumentFetch = ({ apiPrefix = '/api' } = {}) => {
  if (typeof window === 'undefined' || !window.fetch || window.__activityFetchPatched) return;

  const IGNORE_URL_PATTERNS = [/\/api\/activity\/flush/i, /\/api\/Alerts/i];
  const BACKGROUND_URL_PATTERNS = [/\/api\/Alerts/i];

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const [input, init = {}] = args;
    const url = typeof input === 'string' ? input : input.url;
    const method = (init.method || 'GET').toUpperCase();

    if (!(url?.startsWith(apiPrefix) || url?.includes(apiPrefix))) {
      return originalFetch(...args);
    }

    const start = performance.now();
    const isIgnored = IGNORE_URL_PATTERNS.some((pattern) => pattern.test(url));
    const isBackground = BACKGROUND_URL_PATTERNS.some((pattern) => pattern.test(url));
    try {
      const resp = await originalFetch(...args);
      const trackingMode = getApiTrackingMode({
        method,
        status: resp.status,
        latencyMs: Math.round(performance.now() - start),
        isBackground,
      });

      if (!isIgnored && trackingMode === 'keep') {
        trackEvent(
          {
            type: 'api',
            route: window.location.pathname,
            api: {
              path: url,
              method,
              status: resp.status,
              latencyMs: Math.round(performance.now() - start),
            },
          },
          { background: false },
        );
      }
      return resp;
    } catch (err) {
      if (!isIgnored) {
        trackEvent(
          {
            type: 'api',
            route: window.location.pathname,
            api: {
              path: url,
              method,
              status: 0,
              latencyMs: Math.round(performance.now() - start),
            },
            error: { message: err?.message },
          },
          { background: isBackground },
        );
      }
      throw err;
    }
  };

  window.__activityFetchPatched = true;
};

export const instrumentTyping = () => {
  if (typeof document === 'undefined' || window.__activityTypingPatched) return;
  let typingTimeout = null;
  const markActivity = (e) => {
    const target = e.target;
    const targetType = target?.tagName?.toLowerCase();
    const label =
      target?.getAttribute?.('aria-label') ||
      target?.getAttribute?.('placeholder') ||
      target?.getAttribute?.('name') ||
      targetType;
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    typingTimeout = setTimeout(() => {
      // Only mark activity (keep-alive); do not log an event into the buffer.
      if (!isLeader) {
        claimLeadership({ force: true });
      }

      if (isLeader) {
        markUserActivity(Date.now());
      }
      typingTimeout = null;
    }, 500);
  };
  document.addEventListener('keydown', markActivity, true);
  document.addEventListener('keyup', markActivity, true);
  window.__activityTypingPatched = true;
};

export const instrumentClicks = () => {
  if (typeof document === 'undefined' || window.__activityClickPatched) return;

  const getLabelText = (el) => {
    if (!el) return undefined;
    const aria = el.getAttribute('aria-label');
    if (aria) return aria;
    const ariaLabelledBy = el.getAttribute('aria-labelledby');
    if (ariaLabelledBy) {
      const ids = ariaLabelledBy.split(' ').filter(Boolean);
      for (const id of ids) {
        const ref = document.getElementById(id);
        if (ref?.textContent?.trim()) return ref.textContent.trim().slice(0, 120);
      }
    }
    const title = el.getAttribute('title');
    if (title) return title;
    const placeholder = el.getAttribute('placeholder');
    if (placeholder) return placeholder;
    const name = el.getAttribute('name');
    const id = el.id;
    if (id) {
      const forLabel = document.querySelector(`label[for="${id}"]`);
      if (forLabel?.textContent?.trim()) return forLabel.textContent.trim().slice(0, 120);
    }
    const nearestLabel = el.closest('label');
    if (nearestLabel?.textContent?.trim()) return nearestLabel.textContent.trim().slice(0, 120);
    // Try sibling text (common for checkbox wrappers)
    const siblingText = el.nextElementSibling?.textContent?.trim() || el.previousElementSibling?.textContent?.trim();
    if (siblingText) return siblingText.slice(0, 120);
    const parentText = el.parentElement?.textContent?.trim();
    if (parentText) return parentText.slice(0, 120);
    // Walk up a few ancestors to find meaningful text (e.g., table row or container)
    let current = el.parentElement;
    let depth = 0;
    while (current && depth < 4) {
      const text = current.textContent?.trim();
      if (text && text.length > 0) return text.slice(0, 120);
      current = current.parentElement;
      depth += 1;
    }
    if (el.textContent?.trim()) return el.textContent.trim().slice(0, 120);
    const valueAttr = el.getAttribute('value');
    if (valueAttr) return valueAttr;
    if (name) return name;
    return undefined;
  };

  document.addEventListener('click', (e) => {
    const el = e.target.closest(
      '[data-track], button, a, [role="button"], input, select, option, textarea, [role="switch"], [role="checkbox"]',
    );
    if (!el) return;

    const action = el.getAttribute('data-track') || el.tagName?.toLowerCase() || 'click';
    const label = getLabelText(el) || el.id || el.getAttribute('type') || undefined;

    const tag = el.tagName?.toLowerCase();
    const type = (el.getAttribute('type') || '').toLowerCase();
    let state;
    let value;
    if (tag === 'input' && (type === 'checkbox' || type === 'radio')) {
      state = `${type}-${el.checked ? 'on' : 'off'}`;
      value = el.value || el.getAttribute('value') || (el.checked ? 'checked' : 'unchecked');
    } else if (tag === 'input' || tag === 'textarea') {
      state = `${tag}-edited`;
      value = el.value ? String(el.value).slice(0, 120) : undefined;
    } else if (tag === 'select' || tag === 'option') {
      state = `${tag}-selected`;
      const selected = el.selectedOptions?.[0] || el;
      value = selected?.textContent?.trim()?.slice(0, 120) || undefined;
    } else if (el.getAttribute('role') === 'switch' || el.getAttribute('role') === 'checkbox') {
      state = `${el.getAttribute('role')}-${el.getAttribute('aria-checked') || 'toggle'}`;
    }

    const parentTrack = el.closest('[data-track]');

    trackEvent({
      type: 'click',
      route: window.location.pathname,
      action,
      meta: {
        label,
        id: el.id || undefined,
        className: el.className || undefined,
        state,
        parent: parentTrack && parentTrack !== el ? parentTrack.getAttribute('data-track') : undefined,
        value,
      },
    });
  });

  window.__activityClickPatched = true;
};
